/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';
import { isKeyDefined } from '../../utils/UNSAFE_keys';
import { CurrentKeyDetail } from '../../UNSAFE_Collection';
import { useUser } from '../UNSAFE_useUser';

/**
 * Hook for handling current key update due to user interaction including keyboard navigation.
 * This can be reuse by all Collection components.
 *
 * @param keyExtractor function to extract the key based on the provided element
 * @param allowShiftKey if set to false, then pressing arrow key while shift key is down will not change the current key
 * @param isTabbable whether the component is currently in tabbable mode
 * @param getVerticalPrevKey function to get the previous key in the vertical direction based on the current key
 * @param getVerticalNextKey function to get the next key in the vertical direction based on the current key
 * @param getHorizontalPrevKey function to get the previous key in the horizontal direction based on the current key
 * @param getHorizontalNextKey function to get the next key based on the current key
 * @param currentKey the current key
 * @param onCurrentChange function to invoke if the current key has changed
 * @param keyComparator an optional function to compare keys, otherwise === is used
 * @returns
 */
export function useCurrentKey<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  allowShiftKey: boolean,
  isTabbable: boolean,
  getVerticalPrevKey?: () => K | null,
  getVerticalNextKey?: () => K | null,
  getHorizontalPrevKey?: () => K | null,
  getHorizontalNextKey?: () => K | null,
  currentKey?: K,
  onChange?: (detail: CurrentKeyDetail<K>) => void,
  keyComparator?: (key1: K, key2: K) => boolean
) {
  const { direction } = useUser();

  const keyEquals = useCallback(
    (key1: K, key2: K) => {
      if (keyComparator) {
        return keyComparator(key1, key2);
      }
      return key1 === key2;
    },
    [keyComparator]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (onChange && isKeyDefined(currentKey && !isTabbable)) {
        const isRtl = direction === 'rtl';
        const keyHandlers: Record<string, (() => K | null) | undefined> = {
          ArrowUp: getVerticalPrevKey,
          ArrowDown: getVerticalNextKey,
          ArrowLeft: isRtl ? getHorizontalNextKey : getHorizontalPrevKey,
          ArrowRight: isRtl ? getHorizontalPrevKey : getHorizontalNextKey
        };

        // should not update currentKey during keyboard reordering
        if (
          Object.keys(keyHandlers).includes(event.key) &&
          (allowShiftKey || !event.shiftKey) &&
          !event.metaKey &&
          !event.ctrlKey
        ) {
          const newKey = keyHandlers[event.key]?.();
          if (isKeyDefined(newKey) && !keyEquals(currentKey as K, newKey as K)) {
            onChange({ value: newKey as K });
          }
          event.preventDefault();
        }
      }
    },
    [
      currentKey,
      isTabbable,
      onChange,
      getVerticalPrevKey,
      getVerticalNextKey,
      getHorizontalPrevKey,
      getHorizontalNextKey,
      keyEquals,
      allowShiftKey,
      direction
    ]
  );

  const onClickCapture = useCallback(
    (event: MouseEvent) => {
      if (onChange && (allowShiftKey || !event.shiftKey)) {
        const nextKey = keyExtractor(event.target as HTMLElement);
        if (isKeyDefined(nextKey) && !keyEquals(currentKey as K, nextKey as K)) {
          onChange({ value: nextKey as K });
        }
      }
    },
    [currentKey, allowShiftKey, keyExtractor, onChange, keyEquals]
  );

  const currentKeyProps = onChange == null ? {} : { onClickCapture, onKeyDown };
  return { currentKeyProps };
}
